import React, { useEffect, useRef, useState } from "react";
import { fetchStoryAnalytics } from "../../api/storyApi";

import { API_BASE } from "../../api/api";
import { getSocket } from "../../socket";

import StoryProgress from "./StoryProgress";
import StoryActions from "./StoryActions";
import StoryReplies from "./StoryReplies";
import StoryReactions from "./StoryReactions";
import StoryAnalytics from "./StoryAnalytics";


const StoryViewer = ({
  story,
  onClose,
  onLike,
  onShare,
}) => {
  const socket = getSocket();
  const videoRef = useRef();
  const audioRef = useRef();


  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const [reactions, setReactions] = useState([]);
  const [replies, setReplies] = useState([]);

  const [showReplies, setShowReplies] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);

const [touchStart, setTouchStart] = useState(0);


const media = story?.media?.[0];

const music = story?.music;
const text = story?.text;

const stickers = story?.stickers || [];

const backgroundColor =
  story?.backgroundColor || "#000000";


  /* ================= SYNC STORY DATA ================= */
  useEffect(() => {
    setReactions(story?.reactions || []);
    setReplies(story?.replies || []);
    setProgress(0);
  }, [story]);

  /* ================= SOCKET: REACTIONS ================= */
  useEffect(() => {
    if (!socket || !story?._id) return;

    const handleReactionUpdate = ({ storyId, reactions }) => {
      if (storyId !== story._id) return;
      setReactions(reactions || []);
    };

    socket.on("story-reacted", handleReactionUpdate);

    return () => {
      socket.off("story-reacted", handleReactionUpdate);
    };
  }, [socket, story?._id]);

  /* ================= SOCKET: REPLIES ================= */
  useEffect(() => {
    if (!socket || !story?._id) return;

    const handleReply = ({ storyId, reply }) => {
      if (storyId !== story._id) return;
      setReplies((prev) => [...prev, reply]);
    };

    socket.on("story-reply", handleReply);

    return () => {
      socket.off("story-reply", handleReply);
    };
  }, [socket, story?._id]);

  /* ================= PLAY / PAUSE ================= */
  useEffect(() => {
  if (paused) {
    videoRef.current?.pause();
    audioRef.current?.pause();
  } else {
    videoRef.current?.play().catch(() => {});
audioRef.current?.play().catch(() => {});
  }
}, [paused]);

  /* ================= STORY VIEW ================= */
  useEffect(() => {
    if (!story?._id) return;

    const token = localStorage.getItem("token");

    const recordView = async () => {
      try {
        await fetch(`${API_BASE}/api/stories/view/${story._id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      } catch (err) {
        console.error("View error:", err);
      }
    };

    recordView();
  }, [story]);

  /* ================= PROGRESS ================= */
  useEffect(() => {
  if (media?.type !== "video" && !paused) {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);

          if (window.nextStory) {
            window.nextStory();
          } else {
            onClose?.();
          }

          return 100;
        }

        return prev + 1;
      });
    }, 100);

    return () => clearInterval(interval);
  }

  const video = videoRef.current;

  if (!video) return;

  const updateProgress = () => {
  if (!video.duration) return;

  const percent =
    (video.currentTime / video.duration) * 100;

  setProgress(percent);

  if (percent >= 99.8) {
    window.nextStory?.();
  }
};

  video.addEventListener(
    "timeupdate",
    updateProgress
  );

  return () =>
    video.removeEventListener(
      "timeupdate",
      updateProgress
    );

}, [media, paused]);


useEffect(() => {
  return () => {
    videoRef.current?.pause();
    audioRef.current?.pause();
  };
}, []);


const handleTouchStart = (e) => {
  setTouchStart(e.touches[0].clientX);
};

const handleTouchEnd = (e) => {
  const endX = e.changedTouches[0].clientX;
  const distance = endX - touchStart;

  if (distance < -60) {
    window.nextStory?.();
  }

  if (distance > 60 && window.previousStory) {
    window.previousStory();
  }
};

  /* ================= REACTION HANDLER ================= */
  const handleReaction = async (reaction) => {
    try {
      const token = localStorage.getItem("token");

      await fetch(`${API_BASE}/api/stories/react/${story._id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reaction }),
      });

      setReactions((prev) => [...prev, { type: reaction }]);
      setShowReactions(false);

    } catch (err) {
      console.error("Reaction error:", err);
    }
  };

  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

//ERROR LOGGING 
console.log("Story textStyle:", story.textStyle);
console.log("Story stickers:", story.stickers);

  return (
  <div
    className="fixed inset-0 bg-black z-[999] flex items-center justify-center"
    onTouchStart={handleTouchStart}
    onTouchEnd={handleTouchEnd}
  >


   {/* PREVIOUS STORY */}
<div
  className="absolute left-0 top-16 bottom-28 w-1/2 z-10"
  onClick={() => window.previousStory?.()}
/>

{/* NEXT STORY */}
<div
  className="absolute right-0 top-16 bottom-28 w-1/2 z-10"
  onClick={() => window.nextStory?.()}
/>


      {/* CLOSE */}
      <button
        onClick={onClose}
        className="absolute top-5 right-5 text-white text-3xl z-50"
      >
        ✕
      </button>

      {/* ANALYTICS BUTTON (OWNER ONLY) */}
{story?.user?._id === currentUser?._id && (
  <button
    onClick={() => setShowAnalytics(true)}
    className="absolute top-5 left-5 text-white text-xl"
  >
    📊
  </button>
)}

      {/* PROGRESS */}
      <div className="absolute top-3 left-3 right-3 z-50">
        <StoryProgress progress={progress} />
      </div>

      {/* BACKGROUND STORY */}
{!media && (
  <div
    className="absolute inset-0"
    style={{
      background: backgroundColor,
    }}
  />
)}

{/* IMAGE */}
{media?.type === "image" && (
  <img
    src={media.url}
    className="w-full h-full object-contain"
  />
)}

{/* VIDEO */}
{media?.type === "video" && (
  <video
  ref={videoRef}
  src={media.url}
  poster={media.thumbnail}
  className="w-full h-full object-contain"
  autoPlay
  playsInline
  preload="metadata"
  onClick={() => setPaused(!paused)}
  onEnded={() => {
    audioRef.current?.pause();

    if (window.nextStory) {
      window.nextStory();
    } else {
      onClose?.();
    }
  }}
/>
)}


{music?.audioUrl && (
  <audio
    ref={audioRef}
    autoPlay
    controls
    src={music.audioUrl}
    className="
      absolute
      top-20
      right-3
      w-52
      z-50
    "
  />
)}


{text && (
  <div
  className="absolute font-bold z-40"
  style={{
  left: `${story.textStyle?.x}px`,
  top: `${story.textStyle?.y}px`,
    fontSize: story.textStyle?.fontSize,
    color: story.textStyle?.color,
    transform: `rotate(${story.textStyle?.rotation || 0}deg)`,
    textShadow: "0 2px 6px rgba(0,0,0,.8)",
  }}
>
  {text}
</div>
)}


{stickers.map((sticker, i) => (
  <div
    key={i}
    className="
      absolute
      text-5xl
      z-40
    "
    style={{
  left: sticker.x,
  top: sticker.y,
  fontSize: `${(sticker.scale || 1) * 60}px`,
  transform: `rotate(${sticker.rotation || 0}deg)`,
}}
  >
    {sticker.emoji || sticker}
  </div>
))}


      {/* USER INFO */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center gap-3 bg-gradient-to-b from-black/70 to-transparent">
  <img
    src={story.user?.profilePic || "/default-avatar.png"}
    className="w-10 h-10 rounded-full object-cover"
  />

  <div>
    <p className="text-white font-semibold">
      {story.user?.name}
    </p>

    <p className="text-xs text-gray-300">
      Story
    </p>
  </div>
</div>

      {/* BOTTOM ACTIONS */}
      <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black/80 to-transparent">

        {story.caption && (
  <p className="text-white text-sm mb-2">
    {story.caption}
  </p>
)}


{music && (
  <p className="text-xs text-green-400">
    🎵 Music Story
  </p>
)}

{text && !media && (
  <p className="text-xs text-yellow-400">
    ✍️ Text Story
  </p>
)}


{music?.title && (
  <p className="text-white text-xs mb-2">
    🎵 {music.title}
    {music.artist
      ? ` • ${music.artist}`
      : ""}
  </p>
)}

<div className="text-white text-sm mb-3">
  {reactions.length || 0} reactions
</div>

<StoryActions
  story={story}
  onLike={() => onLike(story)}
  onReply={() => setShowReplies(true)}
  onShare={() => onShare(story)}
/>
      </div>

      {/* BUTTONS */}
      <button
        onClick={() => setShowReactions(!showReactions)}
        className="absolute bottom-20 right-5 text-white text-2xl"
      >
        😊
      </button>

      <div className="absolute bottom-20 left-5 text-white text-sm">
        👁 {story.viewsCount || 0}
      </div>

      {/* REACTIONS */}
      {showReactions && (
        <StoryReactions onReact={handleReaction} />
      )}

      {/* REACTION COUNTS */}
      <div className="absolute bottom-32 left-5 flex gap-3 text-white text-sm">
  <span>❤️ {reactions.filter(r => r?.type === "❤️").length}</span>
  <span>😂 {reactions.filter(r => r?.type === "😂").length}</span>
  <span>😮 {reactions.filter(r => r?.type === "😮").length}</span>
</div>

      {/* REPLIES */}
      {showReplies && (
        <StoryReplies
          story={story}
          onClose={() => setShowReplies(false)}
        />
      )}

      {/* ANALYTICS MODAL */}
      {showAnalytics && (
        <StoryAnalytics
          story={story}
          onClose={() => setShowAnalytics(false)}
        />
      )}
    </div>
  );
};

export default StoryViewer;